import { useState } from 'react';
import { Swords, Shield, Wand2, Wind } from 'lucide-react';
import type { PlayerAction, PlayerCombatState } from '@/engine/combat';
import { anotarHechizosConocidos } from '@/engine/sorcery';
import { Button } from '@/components/ui';

interface Props {
  onAction: (a: PlayerAction) => void;
  deshabilitado: boolean;
  player: PlayerCombatState;
}

export function ActionBar({ onAction, deshabilitado }: Props) {
  const [mostrarHechizos, setMostrarHechizos] = useState(false);
  const hechizos = anotarHechizosConocidos('combate');

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-4 gap-1.5 md:gap-2">
        <Button
          onClick={() => onAction({ kind: 'atacar' })}
          disabled={deshabilitado}
          variant="primary"
          size="sm"
        >
          <span className="inline-flex items-center gap-1.5">
            <Swords className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Atacar</span>
          </span>
        </Button>
        <Button
          onClick={() => onAction({ kind: 'defender' })}
          disabled={deshabilitado}
          variant="secondary"
          size="sm"
        >
          <span className="inline-flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Defender</span>
          </span>
        </Button>
        <Button
          onClick={() => setMostrarHechizos((v) => !v)}
          disabled={deshabilitado || hechizos.length === 0}
          variant="ghost"
          size="sm"
        >
          <span className="inline-flex items-center gap-1.5">
            <Wand2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Hechizo</span>
          </span>
        </Button>
        <Button
          onClick={() => onAction({ kind: 'huir' })}
          disabled={deshabilitado}
          variant="ghost"
          size="sm"
        >
          <span className="inline-flex items-center gap-1.5">
            <Wind className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Huir</span>
          </span>
        </Button>
      </div>

      {mostrarHechizos && (
        <div className="rounded border border-ash-800 bg-ash-950/60 p-2 space-y-1.5 max-h-[25vh] scroll-y">
          {hechizos.length === 0 ? (
            <p className="fluid-xs italic text-ash-500 font-ui">No conocés hechizos de combate.</p>
          ) : (
            hechizos.map(({ spell, puedeCastear, motivo }) => (
              <button
                key={spell.id}
                type="button"
                disabled={!puedeCastear || deshabilitado}
                onClick={() => {
                  onAction({ kind: 'hechizo', spellId: spell.id });
                  setMostrarHechizos(false);
                }}
                className="w-full text-left px-2.5 py-1.5 rounded border border-mystic-900/60 hover:border-mystic-700 disabled:opacity-50 disabled:cursor-not-allowed bg-ash-950/40"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-display text-mystic-200 fluid-sm truncate">
                    {spell.nombre}
                  </span>
                  <span className="fluid-xs text-ash-500 font-ui shrink-0">
                    {spell.costoMana} MP
                  </span>
                </div>
                <p className="fluid-xs text-ash-400 mt-0.5 clamp-2">{spell.descripcion}</p>
                {motivo && <p className="fluid-xs text-ember-400 mt-0.5">{motivo}</p>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
